import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type RefObject
} from "react";
import "./scope-table.scss";
import type { AgGridReact } from "ag-grid-react";
import type {
  ColDef,
  ColGroupDef,
  FilterChangedEvent,
  ICellRendererParams,
  IRowNode,
  PostSortRowsParams,
  RowGroupOpenedEvent
} from "ag-grid-enterprise";
import { Button, Tag, Tooltip, Typography } from "antd";
import {
  ArrowsAltOutlined,
  EditOutlined,
  PlusOutlined,
  ShrinkOutlined
} from "@ant-design/icons";
import {
  codeOf,
  formatLodLabel,
  LOD_LABELS,
  type Classification,
  type Classifications,
  type CsiSystem,
  type DsmScopeItem
} from "design-scope";
import CiqAgGrid from "components/ciq-ag-grid";
import {
  GridNoRowsOverlay,
  GridNoRowsOverlayContext
} from "components/blank-state";
import { TruncatedTextWithTooltip } from "components/cell-renders";
import AgGridPrintLayout from "pages/print-view-layout/ag-grid-print-layout";
import type { PrintViewLayoutState } from "pages/print-view-layout/model";
import type {
  ConditionalFormattingColumnConfig,
  ConditionalFormattingRule
} from "components/conditional-formatting";
import { getConditionalFormattingHeaderMenuItems } from "components/conditional-formatting/conditional-formatting-header-menu";
import { useConditionalFormatting } from "components/conditional-formatting/use-conditional-formatting";
import { excelStyles } from "components/excel-ag-grid-export/excel-style";
import {
  EMPTY_BID_PACKAGE_LABEL,
  EMPTY_DESIGN_PACKAGE_LABEL
} from "../shared/design-scope-package-column-values";
import {
  designScopePrintPackageRowId,
  displayedDesignPackageIndex,
  expandDesignScopePrintRows,
  isDesignScopePrintContinuation,
  keepPrintContinuationRowsWithSource
} from "../shared/design-scope-print-rows";
import { ProjectContext } from "context/ProjectProvider";
import type { AgGridTreeExpandGridHandlers } from "hooks/use-ag-grid-tree-expand-toggle";
import type { ProjectEntities } from "../hooks/use-project-entities";
import { ClassBadge } from "./scope-ui";

function primaryCommit(item: DsmScopeItem) {
  return item.commitments[0];
}

/** Print continuation rows show a later link; the log and the first print row use the first. */
function commitForDisplay(item: DsmScopeItem) {
  const index = displayedDesignPackageIndex(item);
  return item.commitments[index] ?? primaryCommit(item);
}

/** Direct scope items first, then nested groups. Stable within each set. */
function placeScopeItemsBeforeNestedGroups(
  params: PostSortRowsParams<DsmScopeItem>
) {
  const leaves: IRowNode<DsmScopeItem>[] = [];
  const groups: IRowNode<DsmScopeItem>[] = [];
  for (const node of params.nodes) {
    if (node.group) groups.push(node);
    else leaves.push(node);
  }
  params.nodes.splice(0, params.nodes.length, ...leaves, ...groups);
}

function childGroupNodes(node?: IRowNode<DsmScopeItem> | null) {
  return (node?.childrenAfterGroup ?? []).filter((child) => child.group);
}

function descendantGroupNodes(node: IRowNode<DsmScopeItem>) {
  const groups: IRowNode<DsmScopeItem>[] = [];
  const walk = (current: IRowNode<DsmScopeItem>) => {
    for (const child of childGroupNodes(current)) {
      groups.push(child);
      walk(child);
    }
  };
  walk(node);
  return groups;
}

/**
 * Expand/collapse control, drawn to the left of the group caret. Shown only
 * when this group has child groups. A click opens or closes every group under
 * the row. Open groups use the outward-arrow icon.
 */
function GroupDepthToggle({
  node,
  onToggleOneLevel
}: {
  node: IRowNode<DsmScopeItem>;
  onToggleOneLevel: (node: IRowNode<DsmScopeItem>) => void;
}) {
  const [expanded, setExpanded] = useState(Boolean(node.expanded));

  useEffect(() => {
    setExpanded(Boolean(node.expanded));
    const sync = () => setExpanded(Boolean(node.expanded));
    node.addEventListener?.("expandedChanged", sync);
    return () => node.removeEventListener?.("expandedChanged", sync);
  }, [node]);

  if (childGroupNodes(node).length === 0) return null;

  const toggleImmediate = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const nextExpanded = !node.expanded;
    onToggleOneLevel(node);
    setExpanded(nextExpanded);
  };

  return (
    <Tooltip title={expanded ? "Collapse Section" : "Expand Section"}>
      <Button
        type="text"
        size="small"
        className="dsm-group-depth-toggle"
        data-testid="scope-group-depth-toggle"
        data-expanded={expanded ? "true" : "false"}
        icon={expanded ? <ArrowsAltOutlined /> : <ShrinkOutlined />}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={toggleImmediate}
      />
    </Tooltip>
  );
}
function primaryBid(item: DsmScopeItem) {
  return item.bids.find((b) => b.primary) ?? item.bids[0];
}

type TreeSystem = CsiSystem;

const ROW_HEIGHT = 42;

/**
 * Value for a UniFormat / MasterFormat column: the system's own code, or the
 * custom label when the item is uncoded in that standard (design shows Custom
 * in both columns for label-only rows).
 */
function classificationForColumn(
  c: Classifications | undefined,
  system: CsiSystem
): Classification | undefined {
  return codeOf(c, system) ?? c?.label;
}

/** Filter text stays the stored code + title. CF reads the rendered label. */
function classificationFilterText(
  c: Classifications | undefined,
  system: CsiSystem
): string {
  const hit = classificationForColumn(c, system);
  if (!hit) return "";
  return [hit.code, hit.title].filter(Boolean).join(" ");
}

function classificationDisplayText(
  c: Classifications | undefined,
  system: CsiSystem
): string {
  const hit = classificationForColumn(c, system);
  if (!hit) return "";
  if (hit.system === "CUSTOM") {
    return hit.title ? `Custom ${hit.title}` : "Custom";
  }
  return [hit.code, hit.title].filter(Boolean).join(" ");
}

function appendExtraCount(label: string, extraCount: number) {
  if (extraCount <= 0) return label;
  return `${label} +${extraCount}`;
}

const EMPTY_CONDITIONAL_FORMATTING_RULES: ConditionalFormattingRule[] = [];

/**
 * CF operators per column (Material-aligned):
 * - Title and Description → text (contains / free-form, like Material name)
 * - Catalog / package / LOD columns → set ("Select values")
 * LOD options are fixed; other set columns harvest from cell values.
 */
export const DESIGN_SCOPE_CONDITIONAL_FORMATTING_COLUMN_CONFIG: Record<
  string,
  ConditionalFormattingColumnConfig
> = {
  title: { filterType: "text" },
  description: { filterType: "text" },
  uniformat: { filterType: "set" },
  masterformat: { filterType: "set" },
  designPackage: { filterType: "set" },
  bidPackage: { filterType: "set" },
  milestone: { filterType: "set" },
  lod: {
    filterType: "set",
    options: ["L1", "L2", "L3", "L4", "L5"]
  }
};

// Tree-path segments carry the full level in the group KEY so group rows can
// render a real classification (badge + code + title), not just a string.
// "|" cannot appear in CSI codes, and the title takes the remainder of the
// segment, so codes/titles containing spaces survive the round trip.
const SEG_SEP = "|";
const encodeSeg = (c: Classification) =>
  [c.system, c.code, c.title].join(SEG_SEP);
const decodeSeg = (seg: string): Classification => {
  const [system, code, ...rest] = seg.split(SEG_SEP);
  return {
    system: system as Classification["system"],
    code,
    title: rest.join(SEG_SEP)
  };
};

/**
 * The one group every item without a code in the active system hangs under —
 * items coded only in the other standard, label-only items, and unclassified
 * ones. They share a group rather than floating at the root so the tree has a
 * single, sortable "everything else" bucket at the bottom.
 */
const UNCLASSIFIED = "__unclassified__";

/**
 * The ancestor chain an item hangs under in the active system, or the
 * unclassified bucket when it holds no code there. Uses the stored
 * `parent_id` path — never inferred from the code string.
 */
function pathFor(item: DsmScopeItem, system: TreeSystem): string[] {
  const c = codeOf(item.classification, system);
  if (!c?.code) return [UNCLASSIFIED];
  const stored = c.path && c.path.length > 0 ? c.path : [];
  // The chain has to end at the item's own code. A catalog that stores only
  // the ancestors loses that level, and the item groups under its parent.
  const nodes =
    stored[stored.length - 1]?.number === c.code
      ? stored
      : [...stored, { number: c.code, title: c.title }];
  return nodes.map((node) =>
    encodeSeg({
      system,
      code: node.number,
      title: node.title
    })
  );
}

/** Distinct scope items under a tree group row (leaves only). */
function leafItems(node: IRowNode): DsmScopeItem[] {
  const out: DsmScopeItem[] = [];
  for (const n of node.allLeafChildren ?? []) {
    const d = n.data as DsmScopeItem | undefined;
    if (d && !isDesignScopePrintContinuation(d)) out.push(d);
  }
  return out;
}

/** A "+N" chip whose tooltip lists the extra rows folded behind the primary one. */
function ExtraTag({ names }: { names: string[] }) {
  if (names.length === 0) return null;
  return (
    <Tooltip title={names.join(", ")}>
      <Tag className="ml-1">+{names.length}</Tag>
    </Tooltip>
  );
}

/** Print lists every linked name. The log folds the rest into ExtraTag. */
function LinkedPackageLines({ names }: { names: string[] }) {
  return (
    <span className="flex flex-col py-0.5 leading-5">
      {names.map((name, index) => (
        <span key={`${name}-${index}`}>{name}</span>
      ))}
    </span>
  );
}

function bidPackageNames(
  item: DsmScopeItem,
  nameOfBp: (id: string) => string
): string[] {
  const primary = primaryBid(item);
  if (!primary?.bidPackageId) return [];
  const ordered = [primary, ...item.bids.filter((bid) => bid !== primary)];
  return ordered
    .map((bid) => (bid.bidPackageId ? nameOfBp(bid.bidPackageId).trim() : ""))
    .filter(Boolean);
}

type Ctx = {
  entities: ProjectEntities;
  onEdit?: (item: DsmScopeItem) => void;
  onAddUnder?: (c: Classification | null) => void;
  conditionalFormattingRules?: ConditionalFormattingRule[];
  onConditionalFormattingRulesChange?: (
    rules: ConditionalFormattingRule[]
  ) => void;
  conditionalFormattingColumnConfig?: Record<
    string,
    ConditionalFormattingColumnConfig
  >;
};
type Params = ICellRendererParams<DsmScopeItem> & { context: Ctx };

export default function ScopeTable(props: {
  items: DsmScopeItem[];
  entities: ProjectEntities;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Omitted for read-only (archived) projects; hides the row Edit action. */
  onEdit?: (item: DsmScopeItem) => void;
  /** Tree mode: same grid, same columns, rows grouped by classification. */
  hierarchy?: boolean;
  /** Which classification system the tree groups by. */
  treeSystem?: TreeSystem;
  /** Tree mode: group rows offer "add a scope item under this classification". */
  onAddUnder?: (c: Classification | null) => void;
  /** Lifted so the page's toolbar can drive expandAll/collapseAll. */
  gridRef?: RefObject<AgGridReact>;
  /** Checkbox multi-select changes (separate from detail-panel focus). */
  onSelectionChanged?: () => void;
  /** Optional sync for a single Expand all / Collapse all toolbar control. */
  treeExpandSync?: AgGridTreeExpandGridHandlers;
  /** Called after first data render (e.g. re-apply saved Views after remount). */
  onFirstDataRendered?: () => void;
  /** Column filter changes (keeps toolbar package dropdowns in sync). */
  onFilterChanged?: (event: FilterChangedEvent<DsmScopeItem>) => void;
  /** Rows re-filtered / re-sorted or row data replaced (e.g. visible count). */
  onModelUpdated?: () => void;
  onSortChanged?: () => void;
  onColumnVisible?: (e: { api?: unknown; source?: string }) => void;
  onColumnResized?: (e: {
    finished?: boolean;
    source?: string;
    api?: unknown;
  }) => void;
  onDragStopped?: (e: {
    api?: unknown;
    target?: { className?: string };
  }) => void;
  /** Page-owned so list/hierarchy remounts keep the same rules. */
  conditionalFormattingRules?: ConditionalFormattingRule[];
  onConditionalFormattingRulesChange?: (
    rules: ConditionalFormattingRule[]
  ) => void;
  /** Print/PDF view: reuse these columns inside the shared print layout. */
  printLayout?: {
    state: PrintViewLayoutState;
    rowLoading?: boolean;
  };
  /** Shown under the column headers when the grid has no rows to display. */
  noRowsOverlay?: ReactNode;
}) {
  const {
    items,
    entities,
    selectedId,
    onSelect,
    onEdit,
    hierarchy = false,
    treeSystem = "UF",
    onAddUnder,
    gridRef: externalGridRef,
    onSelectionChanged,
    treeExpandSync,
    onFirstDataRendered,
    onFilterChanged,
    onModelUpdated,
    onSortChanged,
    onColumnVisible,
    onColumnResized,
    onDragStopped,
    conditionalFormattingRules = EMPTY_CONDITIONAL_FORMATTING_RULES,
    onConditionalFormattingRulesChange,
    printLayout,
    noRowsOverlay
  } = props;
  const forPrint = Boolean(printLayout);
  const {
    columnHeaders: { designScopeHeaderMap }
  } = useContext(ProjectContext);
  const internalGridRef = useRef<AgGridReact>(null);
  const gridRef = externalGridRef ?? internalGridRef;
  // AG Grid never fires firstDataRendered for an empty grid, so the page would
  // never apply its saved layout. Report the first (empty) model update of each
  // grid mount (the grid remounts per list/hierarchy key) instead.
  const gridKey = hierarchy ? treeSystem : "flat";
  const emptyFirstRenderReportedKeyRef = useRef<string | null>(null);
  const baseDefaultColDef = useMemo<ColDef<DsmScopeItem>>(
    () => ({
      sortable: true,
      editable: false,
      filter: true,
      resizable: true,
      menuTabs: ["filterMenuTab"],
      minWidth: 100
    }),
    []
  );
  const {
    conditionalFormattingRules: appliedConditionalFormattingRules,
    conditionalFormattingDefaultColDef,
    conditionalFormattingExcelStyles,
    setConditionalFormattingRules
  } = useConditionalFormatting<DsmScopeItem>({
    gridRef,
    defaultColDef: baseDefaultColDef,
    initialRules: conditionalFormattingRules
  });
  const mergedExcelStyles = useMemo(
    () => [...excelStyles, ...conditionalFormattingExcelStyles],
    [conditionalFormattingExcelStyles]
  );
  useEffect(() => {
    setConditionalFormattingRules(conditionalFormattingRules);
  }, [conditionalFormattingRules, setConditionalFormattingRules]);
  const onGridConditionalFormattingRulesChange = useCallback(
    (rules: ConditionalFormattingRule[]) => {
      setConditionalFormattingRules(rules);
      onConditionalFormattingRulesChange?.(rules);
    },
    [onConditionalFormattingRulesChange, setConditionalFormattingRules]
  );

  // AG Grid snapshots `context` when it renders, so the design/bid package
  // names stay "—" when the entity queries resolve after the first paint —
  // which is the normal case once a project has a few hundred packages.
  useEffect(() => {
    gridRef.current?.api?.refreshCells({ force: true });
  }, [entities, gridRef]);

  const paths = useMemo(() => {
    const out = new Map<string, string[]>();
    if (!hierarchy) return out;
    for (const item of items) {
      out.set(item.id, [...pathFor(item, treeSystem), item.id]);
      if (!forPrint) continue;
      item.commitments.forEach((commitment, index) => {
        if (index === 0 || !commitment.designPackageId) return;
        const id = designScopePrintPackageRowId(item.id, index);
        out.set(id, [...pathFor(item, treeSystem), id]);
      });
    }
    return out;
  }, [items, hierarchy, treeSystem, forPrint]);

  /**
   * Leftovers last in the UNSORTED tree. The autoGroupColumnDef comparator
   * only runs once the user actually sorts a column; until then AG Grid builds
   * the tree in rowData order, and listScopeItems sorts on the active system's
   * root code — which is "" for an item with no code there, so leftovers would
   * otherwise lead the whole grid.
   */
  const rows = useMemo(() => {
    const ordered = !hierarchy
      ? items
      : [...items].sort((a, b) => {
          const last = (item: DsmScopeItem) =>
            paths.get(item.id)?.[0] === UNCLASSIFIED ? 1 : 0;
          return last(a) - last(b);
        });
    return forPrint ? expandDesignScopePrintRows(ordered) : ordered;
  }, [items, hierarchy, paths, forPrint]);

  const columnDefs = useMemo<
    (ColDef<DsmScopeItem> | ColGroupDef<DsmScopeItem>)[]
  >(() => {
    const formatColumn = (
      system: CsiSystem,
      colId: string,
      headerName: string
    ): ColDef<DsmScopeItem> => ({
      headerName,
      colId,
      minWidth: 220,
      filter: "agSetColumnFilter",
      valueGetter: (p) =>
        classificationDisplayText(p.data?.classification, system),
      filterValueGetter: (p) =>
        classificationFilterText(p.data?.classification, system),
      cellRenderer: (p: Params) => {
        if (!p.data) return null;
        if (isDesignScopePrintContinuation(p.data)) return null;
        const hit = classificationForColumn(p.data.classification, system);
        if (!hit) return <Typography.Text type="secondary">—</Typography.Text>;
        return (
          <span className="flex w-full min-w-0 items-center gap-2">
            <span className="shrink-0">
              {hit.system === "CUSTOM" ? (
                <Tag className="dsm-gap-tag">Custom</Tag>
              ) : (
                <ClassBadge system={hit.system} code={hit.code} />
              )}
            </span>
            {hit.title ? (
              <span className="min-w-0 flex-1 overflow-hidden">
                <TruncatedTextWithTooltip text={hit.title} dataTestId={colId} />
              </span>
            ) : (
              <Typography.Text type="secondary">—</Typography.Text>
            )}
          </span>
        );
      }
    });

    const ufColumn = formatColumn(
      "UF",
      "uniformat",
      designScopeHeaderMap?.uniformat || "UniFormat"
    );
    const mfColumn = formatColumn(
      "MF",
      "masterformat",
      designScopeHeaderMap?.masterformat || "MasterFormat"
    );

    // ID leads both views: first in list, and immediately after the
    // auto-group Classification Code column in hierarchy.
    const idColumn: ColDef<DsmScopeItem> = {
      headerName: designScopeHeaderMap?.sequence_id || "ID",
      colId: "sequence_id",
      field: "sequence_id",
      width: 88,
      flex: 0,
      filter: false,
      suppressHeaderMenuButton: true,
      valueGetter: (p) => p.data?.sequence_id ?? "",
      cellRenderer: (p: Params) => {
        if (!p.data || isDesignScopePrintContinuation(p.data)) return null;
        return p.data.sequence_id != null ? (
          <span>{p.data.sequence_id}</span>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        );
      }
    };

    // Title follows ID: in the flat grid after ID, and in the tree after the
    // auto-group Classification Code + ID.
    const titleColumn: ColDef<DsmScopeItem>[] = [
      {
        headerName: designScopeHeaderMap?.title || "Title",
        colId: "title",
        minWidth: 180,
        // Like Material name: free-form text CF (contains), not Select values.
        filter: true,
        valueGetter: (p) => p.data?.title ?? "",
        cellRenderer: (p: Params) => {
          if (!p.data || isDesignScopePrintContinuation(p.data)) return null;
          return p.data.title ? (
            <TruncatedTextWithTooltip text={p.data.title} dataTestId="title" />
          ) : (
            <Typography.Text type="secondary">—</Typography.Text>
          );
        }
      }
    ];

    const descriptionColumn: ColDef<DsmScopeItem> = {
      headerName: designScopeHeaderMap?.description || "Description",
      colId: "description",
      minWidth: 220,
      filter: true,
      valueGetter: (p) => p.data?.description ?? "",
      cellRenderer: (p: Params) => {
        if (!p.data || isDesignScopePrintContinuation(p.data)) return null;
        return p.data.description ? (
          <TruncatedTextWithTooltip
            text={p.data.description}
            dataTestId="description"
          />
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        );
      }
    };

    const linkedDesignPackageGroup: ColGroupDef<DsmScopeItem> = {
      headerName:
        designScopeHeaderMap?.linked_design_package || "Linked Design Package",
      groupId: "linkedDesignPackage",
      headerClass: "dsm-linked-dp-group-header",
      marryChildren: true,
      children: [
        {
          headerName: designScopeHeaderMap?.design_package || "Design Package",
          colId: "designPackage",
          minWidth: 170,
          filter: "agSetColumnFilter",
          filterValueGetter: (p) => {
            const c = p.data && primaryCommit(p.data);
            return c ? p.context.entities.nameOfDp(c.designPackageId) : "";
          },
          valueGetter: (p) => {
            if (!p.data) return "";
            const c = primaryCommit(p.data);
            if (!c?.designPackageId) return EMPTY_DESIGN_PACKAGE_LABEL;
            return appendExtraCount(
              p.context.entities.nameOfDp(c.designPackageId),
              p.data.commitments.slice(1).length
            );
          },
          cellRenderer: (p: Params) => {
            if (!p.data) return null;
            const c = forPrint
              ? commitForDisplay(p.data)
              : primaryCommit(p.data);
            if (!c?.designPackageId) {
              if (isDesignScopePrintContinuation(p.data)) return null;
              return (
                <Tag className="dsm-gap-tag">{EMPTY_DESIGN_PACKAGE_LABEL}</Tag>
              );
            }
            if (forPrint) {
              return (
                <span>{p.context.entities.nameOfDp(c.designPackageId)}</span>
              );
            }
            return (
              <span className="flex w-full min-w-0 items-center">
                <TruncatedTextWithTooltip
                  text={p.context.entities.nameOfDp(c.designPackageId)}
                  dataTestId="design-package"
                  className="min-w-0 flex-1"
                />
                <span className="shrink-0">
                  <ExtraTag
                    names={p.data.commitments
                      .slice(1)
                      .map((x) =>
                        p.context.entities.nameOfDp(x.designPackageId)
                      )}
                  />
                </span>
              </span>
            );
          }
        },
        {
          headerName: designScopeHeaderMap?.milestone || "Milestone",
          colId: "milestone",
          minWidth: 150,
          filter: "agSetColumnFilter",
          valueGetter: (p) => {
            const c = p.data && primaryCommit(p.data);
            return c?.milestoneId
              ? p.context.entities.nameOfMilestone(c.milestoneId)
              : "";
          },
          cellRenderer: (p: Params) => {
            if (!p.data) return null;
            const c = forPrint
              ? commitForDisplay(p.data)
              : primaryCommit(p.data);
            // No linked design package — leave the cell blank (tag lives in
            // the Design Package column only).
            if (!c?.designPackageId) return null;
            return c.milestoneId ? (
              <span>{p.context.entities.nameOfMilestone(c.milestoneId)}</span>
            ) : (
              <Typography.Text type="secondary">—</Typography.Text>
            );
          }
        },
        {
          headerName: designScopeHeaderMap?.lod || "LOD",
          colId: "lod",
          minWidth: 180,
          flex: 0,
          filter: "agSetColumnFilter",
          // Cell shows "L2 Concept Defined"; the set filter keeps "L4 · Detailed".
          valueGetter: (p) => {
            if (!p.data) return "";
            const c = primaryCommit(p.data);
            if (!c?.designPackageId || c.lod == null) return "";
            return `L${c.lod}`;
          },
          filterValueGetter: (p) => {
            const lod = p.data && primaryCommit(p.data)?.lod;
            return lod != null ? formatLodLabel(lod) : "";
          },
          comparator: (a, b) => {
            const rank = (v: string) => {
              const m = /^L([1-5])\b/.exec(v);
              return m ? Number(m[1]) : 0;
            };
            return rank(String(a ?? "")) - rank(String(b ?? ""));
          },
          cellRenderer: (p: Params) => {
            // LOD is a leaf-level fact; group rows stay quiet.
            if (!p.data) return null;
            const c = forPrint
              ? commitForDisplay(p.data)
              : primaryCommit(p.data);
            if (!c?.designPackageId || c.lod == null) return null;
            return (
              <span>
                <span className="font-medium">L{c.lod}</span>{" "}
                {LOD_LABELS[c.lod]}
              </span>
            );
          }
        }
      ]
    };

    const bidPackageColumn: ColDef<DsmScopeItem> = {
      headerName: designScopeHeaderMap?.bid_package || "Bid Package",
      colId: "bidPackage",
      minWidth: 170,
      // Excel only draws the exported line breaks when wrap text is on.
      cellClass: "excelWrapText",
      ...(forPrint ? { wrapText: true, autoHeight: true } : {}),
      filter: "agSetColumnFilter",
      filterValueGetter: (p) => {
        const b = p.data && primaryBid(p.data);
        return b ? p.context.entities.nameOfBp(b.bidPackageId) : "";
      },
      valueGetter: (p) => {
        if (!p.data) return "";
        const b = primaryBid(p.data);
        if (!b) return EMPTY_BID_PACKAGE_LABEL;
        return appendExtraCount(
          p.context.entities.nameOfBp(b.bidPackageId),
          p.data.bids.filter((x) => x !== b).length
        );
      },
      cellRenderer: (p: Params) => {
        if (!p.data || isDesignScopePrintContinuation(p.data)) return null;
        const b = primaryBid(p.data);
        if (!b)
          return <Tag className="dsm-gap-tag">{EMPTY_BID_PACKAGE_LABEL}</Tag>;
        if (forPrint) {
          return (
            <LinkedPackageLines
              names={bidPackageNames(p.data, p.context.entities.nameOfBp)}
            />
          );
        }
        return (
          <span className="flex w-full min-w-0 items-center">
            <TruncatedTextWithTooltip
              text={p.context.entities.nameOfBp(b.bidPackageId)}
              dataTestId="bid-package"
              className="min-w-0 flex-1"
            />
            <span className="shrink-0">
              <ExtraTag
                names={p.data.bids
                  .filter((x) => x !== b)
                  .map((x) => p.context.entities.nameOfBp(x.bidPackageId))}
              />
            </span>
          </span>
        );
      }
    };

    const actionsColumn: ColDef<DsmScopeItem> = {
      headerName: "Actions",
      colId: "actions",
      width: 88,
      flex: 0,
      sortable: false,
      resizable: false,
      filter: false,
      suppressHeaderMenuButton: true,
      pinned: "right",
      lockPinned: true,
      lockVisible: true,
      suppressColumnsToolPanel: true,
      cellRenderer: (p: Params) => {
        if (p.data) {
          if (!p.context.onEdit) return null;
          return (
            <Button
              size="small"
              type="text"
              data-testid={`scope-edit-${p.data.id}`}
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                p.context.onEdit!(p.data!);
              }}
            />
          );
        }
        if (!p.context.onAddUnder) return null;
        const seg = decodeSeg(String(p.node.key ?? ""));
        return (
          <Tooltip title="Add a scope item under this classification">
            <Button
              size="small"
              type="text"
              data-testid={`tree-add-${seg.code || seg.title}`}
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                p.context.onAddUnder?.(seg);
              }}
            />
          </Tooltip>
        );
      }
    };

    const scopeItemCountColumn: ColDef<DsmScopeItem> = {
      headerName:
        designScopeHeaderMap?.scope_item_count || "No. of Scope Items",
      colId: "scopeItemCount",
      // Stays immediately after the pinned UniFormat / MasterFormat group column.
      suppressMovable: true,
      minWidth: 160,
      sortable: true,
      // Group-only leaf counts; set filter only harvests blanks from leaves.
      filter: false,
      valueGetter: (p) => {
        if (!p.node || p.node.group !== true) return "";
        return leafItems(p.node).length;
      },
      comparator: (a, b) => {
        const rank = (value: unknown) =>
          typeof value === "number" ? value : -1;
        return rank(a) - rank(b);
      },
      cellRenderer: (p: Params) => {
        // Counts describe what sits under a classification group — leaves stay blank.
        if (!p.node?.group) return null;
        // Same pill chrome as the page header count badge (getGridStatusBudge).
        return (
          <span className="inline-flex items-center border border-solid border-ciq-gray-300 rounded-xl px-2 py-0.5 text-xs font-normal text-black text-opacity-50">
            {leafItems(p.node).length}
          </span>
        );
      }
    };

    const shared: (ColDef<DsmScopeItem> | ColGroupDef<DsmScopeItem>)[] = [
      linkedDesignPackageGroup,
      bidPackageColumn,
      actionsColumn
    ];

    // List: ID + title + description + both format columns. Hierarchy: the
    // tree column is titled for the active system (UniFormat or MasterFormat).
    // No. of Scope Items is always the next column, then ID, Title, Description.
    if (!hierarchy)
      return [
        idColumn,
        ...titleColumn,
        descriptionColumn,
        ufColumn,
        mfColumn,
        ...shared
      ];
    const otherFormat = treeSystem === "UF" ? mfColumn : ufColumn;
    return [
      scopeItemCountColumn,
      idColumn,
      ...titleColumn,
      descriptionColumn,
      otherFormat,
      ...shared
    ];
  }, [forPrint, hierarchy, treeSystem, designScopeHeaderMap]);

  const getDataPath = useMemo(
    () => (data?: DsmScopeItem) =>
      // AG Grid can invoke this against transitioning row nodes while the
      // tree flag flips — degrade to a flat single-segment path, never throw.
      (data && paths.get(data.id)) ?? [String(data?.id ?? "")],
    [paths]
  );

  const toggleGroupOneLevel = useCallback((node: IRowNode<DsmScopeItem>) => {
    const expanded = !node.expanded;
    if (expanded) {
      node.setExpanded(true);
      descendantGroupNodes(node).forEach((child) => child.setExpanded(true));
      return;
    }
    [...descendantGroupNodes(node)]
      .reverse()
      .forEach((child) => child.setExpanded(false));
    node.setExpanded(false);
  }, []);

  const onRowGroupOpened = useCallback(
    (event: RowGroupOpenedEvent<DsmScopeItem>) => {
      treeExpandSync?.onRowGroupOpened(event);
    },
    [treeExpandSync]
  );

  // Checkboxes own multi-select; row click still opens the detail panel
  // (enableClickSelection: false so the two don't collide). In hierarchy,
  // only leaf scope items are selectable — classification groups have no box.
  // Selection column pins checkboxes at the far left (not indented with the tree).
  const rowSelection = useMemo(
    () =>
      hierarchy
        ? {
            mode: "multiRow" as const,
            headerCheckbox: true,
            // Header checkbox selects only rows the active filters still show.
            selectAll: "filtered" as const,
            enableClickSelection: false,
            groupSelects: "self" as const,
            checkboxLocation: "selectionColumn" as const,
            hideDisabledCheckboxes: true,
            isRowSelectable: (node: IRowNode<DsmScopeItem>) =>
              Boolean(node.data) && !node.group,
            checkboxes: (params: { node: IRowNode<DsmScopeItem> }) =>
              Boolean(params.node.data) && !params.node.group
          }
        : {
            mode: "multiRow" as const,
            checkboxes: true,
            headerCheckbox: true,
            selectAll: "filtered" as const,
            enableClickSelection: false,
            checkboxLocation: "selectionColumn" as const
          },
    [hierarchy]
  );

  const autoGroupColumnDef = useMemo<ColDef<DsmScopeItem> | undefined>(() => {
    if (!hierarchy) return undefined;
    return {
      headerName:
        treeSystem === "UF"
          ? designScopeHeaderMap?.uniformat || "UniFormat"
          : designScopeHeaderMap?.masterformat || "MasterFormat",
      pinned: "left",
      minWidth: 320,
      flex: 2,
      valueGetter: (p: {
        data?: DsmScopeItem;
        node?: { key?: string | null };
      }) => {
        // Leaves carry their own code so every tree level shows (and exports) one.
        if (p.data)
          return codeOf(p.data.classification, treeSystem)?.code ?? "";
        const key = String(p.node?.key ?? "");
        if (key === UNCLASSIFIED) return "Unclassified";
        const seg = decodeSeg(key);
        return [seg.code, seg.title].filter(Boolean).join(" ");
      },
      comparator: (a, b, nodeA, nodeB, isDescending) => {
        const unA = nodeA?.key === UNCLASSIFIED;
        const unB = nodeB?.key === UNCLASSIFIED;
        if (unA !== unB) {
          const last = unA ? 1 : -1;
          return isDescending ? -last : last;
        }
        const groupA = Boolean(nodeA?.group);
        const groupB = Boolean(nodeB?.group);
        if (groupA !== groupB) {
          const last = groupA ? -1 : 1;
          return isDescending ? -last : last;
        }
        return String(a ?? "").localeCompare(String(b ?? ""));
      },
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: (p: Params) => {
          if (p.data) {
            if (isDesignScopePrintContinuation(p.data)) return null;
            const own = codeOf(p.data.classification, treeSystem);
            if (!own?.code) return null;
            return <ClassBadge system={own.system} code={own.code} />;
          }
          const key = String(p.node.key ?? "");
          const toggle = (
            <GroupDepthToggle
              node={p.node}
              onToggleOneLevel={toggleGroupOneLevel}
            />
          );
          if (key === UNCLASSIFIED) {
            return (
              <span className="inline-flex items-center gap-2">
                {toggle}
                <Typography.Text strong type="secondary">
                  Unclassified
                </Typography.Text>
              </span>
            );
          }
          const seg = decodeSeg(key);
          return (
            <span className="inline-flex items-center gap-2">
              {toggle}
              <ClassBadge system={seg.system} code={seg.code} />
              <Typography.Text strong>{seg.title}</Typography.Text>
            </span>
          );
        }
      }
    } as ColDef<DsmScopeItem>;
  }, [designScopeHeaderMap, hierarchy, toggleGroupOneLevel, treeSystem]);

  if (printLayout) {
    return (
      <div className="dsm-scope-grid" data-testid="scope-grid">
        <AgGridPrintLayout
          gridRef={gridRef}
          state={printLayout.state}
          rowData={rows}
          rowLoading={printLayout.rowLoading}
          columnDefEntity={columnDefs}
          autoGroupColumnDef={autoGroupColumnDef}
          treeData={hierarchy}
          getDataPath={hierarchy ? getDataPath : undefined}
          postSortRows={(params) => {
            if (hierarchy) placeScopeItemsBeforeNestedGroups(params);
            keepPrintContinuationRowsWithSource(params);
          }}
          getRowId={(p: { data: DsmScopeItem }) => p.data.id}
          gridContext={{
            entities,
            onEdit,
            onAddUnder,
            conditionalFormattingRules:
              printLayout.state.conditionalsFormatting ?? []
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="dsm-scope-grid flex h-full flex-col"
      data-testid="scope-grid"
    >
      <GridNoRowsOverlayContext.Provider value={noRowsOverlay}>
        <CiqAgGrid
          key={gridKey}
          gridRef={gridRef}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={conditionalFormattingDefaultColDef}
          excelStyles={mergedExcelStyles}
          getMainMenuItems={getConditionalFormattingHeaderMenuItems}
          context={
            {
              entities,
              onEdit,
              onAddUnder,
              conditionalFormattingRules: appliedConditionalFormattingRules,
              onConditionalFormattingRulesChange:
                onGridConditionalFormattingRulesChange,
              conditionalFormattingColumnConfig:
                DESIGN_SCOPE_CONDITIONAL_FORMATTING_COLUMN_CONFIG
            } satisfies Ctx
          }
          getRowId={(p: { data: DsmScopeItem }) => p.data.id}
          rowHeight={ROW_HEIGHT}
          // The whole column set fits one screen; virtualizing columns only makes
          // cells pop in during horizontal scroll (and hides them from tests).
          suppressColumnVirtualisation
          rowSelection={rowSelection}
          // Detail-panel focus is separate from AG Grid checkbox selection
          // (which owns ag-row-selected / --ag-selected-row-background-color).
          // Hierarchy leaves get a tinted default fill so they read apart from
          // white classification group rows.
          rowClassRules={{
            "dsm-scope-detail-focus": (p: { data?: DsmScopeItem }) =>
              Boolean(p.data && p.data.id === selectedId),
            "dsm-scope-tree-leaf": (p: { data?: DsmScopeItem }) =>
              hierarchy && Boolean(p.data)
          }}
          onCellClicked={(e: {
            data?: DsmScopeItem;
            event?: Event | null;
            column?: { getColId?: () => string } | null;
          }) => {
            // Prefer cellClicked over rowClicked — row events have no column,
            // so selection-cell padding couldn't be filtered. Skip checkbox
            // column (whole cell) and action controls.
            const colId = e.column?.getColId?.();
            if (colId === "ag-Grid-SelectionColumn" || colId === "actions")
              return;
            const target = e.event?.target as HTMLElement | undefined;
            if (
              target?.closest(
                "button, a, input, [col-id='ag-Grid-SelectionColumn'], .ag-checkbox-input-wrapper, .ag-selection-checkbox"
              )
            )
              return;
            if (e.data) onSelect(e.data.id);
          }}
          onSelectionChanged={onSelectionChanged}
          onFilterChanged={onFilterChanged}
          onModelUpdated={() => {
            onModelUpdated?.();
            if (
              items.length === 0 &&
              emptyFirstRenderReportedKeyRef.current !== gridKey
            ) {
              emptyFirstRenderReportedKeyRef.current = gridKey;
              onFirstDataRendered?.();
            }
          }}
          noRowsOverlayComponent={GridNoRowsOverlay}
          onSortChanged={onSortChanged}
          onColumnVisible={onColumnVisible}
          onColumnResized={onColumnResized}
          onDragStopped={onDragStopped}
          disableResizingAutoWidth
          treeData={hierarchy}
          getDataPath={getDataPath}
          postSortRows={
            hierarchy ? placeScopeItemsBeforeNestedGroups : undefined
          }
          groupDefaultExpanded={-1}
          onRowGroupOpened={onRowGroupOpened}
          onExpandOrCollapseAll={treeExpandSync?.onExpandOrCollapseAll}
          onFirstDataRendered={(event) => {
            treeExpandSync?.onFirstDataRendered?.(event);
            onFirstDataRendered?.();
          }}
          {...(autoGroupColumnDef ? { autoGroupColumnDef } : {})}
        />
      </GridNoRowsOverlayContext.Provider>
    </div>
  );
}
